<template>
  <div class="subscription-reports">
    <div class="report-card">
      <div class="report-header">
        <img alt="" src="/assets/file.svg">
        <h1 class="report-title">{{ adminText('organizationAll.finance_overview_e666812f', 'Finance overview') }}</h1>
      </div>

      <div class="report-body">
        <div class="date-row">
          <date-field
            v-model="financeFrom"
            :label="adminText('organizationAll.from_date_3b5d11ae', 'From date')"
            :max="financeTo ? financeTo.toString() : todayDate"
          />
          <date-field
            v-model="financeTo"
            :label="adminText('organizationAll.to_date_af244da3', 'To date')"
            :min="financeFrom ? financeFrom.toString() : null"
            :max="todayDate"
          />
        </div>

        <div class="filter-row">
          <v-select
            v-model="selectedSubscriptionId"
            :items="organizationSentinelOptions(subscriptionOptions, 'text')"
            :loading="plansLoading"
            item-text="text"
            item-value="value"
            :label="adminText('organizationAll.subscription_plans_96663a33', 'Subscription plans')"
            box
            hide-details
          />
          <v-select
            v-model="selectedPaymentChannel"
            :items="organizationOptions(paymentChannelOptions, 'text')"
            item-text="text"
            item-value="value"
            :label="adminText('organizationAll.purchase_source_935f9eb8', 'Purchase source')"
            box
            hide-details
          />
        </div>

        <div v-if="plansError" class="filter-error" role="alert">
          <span>{{ organizationText(plansError) }}</span>
          <button type="button" @click="fetchSubscriptionPlans">{{ adminText('organizationAll.retry_9f5cd8a2', 'Retry') }}</button>
        </div>

        <div class="refresh-row">
          <main-button flat :loading="financeSummaryLoading" @click="fetchFinanceSummary">
            <img alt="" src="/assets/reset-icon.svg">
            {{ adminText('organizationAll.refresh_56e3badc', 'Refresh') }}
          </main-button>
        </div>

        <p v-if="financeSummaryError" class="report-error">{{ organizationText(financeSummaryError) }}</p>
        <p v-if="financeLedgerError" class="report-error">{{ organizationText(financeLedgerError) }}</p>
        <p v-if="financeLedgerMessage" class="report-message">{{ organizationText(financeLedgerMessage) }}</p>

        <div v-if="financeSummary && !financeSummaryError" class="summary-grid">
          <div class="summary-row">
            <span>{{ adminText('organizationAll.gross_revenue_bac54e11', 'Gross revenue') }}</span>
            <strong>{{ formatCurrency(financeSummary.grossAmount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.net_revenue_83e899da', 'Net revenue') }}</span>
            <strong>{{ formatCurrency(financeSummary.netAmount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.vat_5f54ad7b', 'VAT') }}</span>
            <strong>{{ formatCurrency(financeSummary.vatAmount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.online_gross_4fa936e9', 'Online gross') }}</span>
            <strong>{{ formatCurrency(financeSummary.onlineGrossAmount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.offline_gross_2779ef37', 'Offline gross') }}</span>
            <strong>{{ formatCurrency(financeSummary.offlineGrossAmount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.orders_cded0933', 'Orders') }}</span>
            <strong>{{ formatNumber(financeSummary.orderCount) }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ adminText('organizationAll.subscriptions_sold_669557f7', 'Subscriptions sold') }}</span>
            <strong>{{ formatNumber(financeSummary.totalQuantity) }}</strong>
          </div>
        </div>

        <div class="actions-row">
          <router-link :to="printableRoute" target="_blank" class="printable-link">
            <main-button flat>{{ adminText('organizationAll.printable_view_198ba010', 'Printable view') }}</main-button>
          </router-link>
          <main-button flat :loading="financeDownloadLoading" @click="downloadFinanceLedger">
            <img alt="" src="/assets/download.svg">
            {{ adminText('organizationAll.download_csv_5d26d1b6', 'Download (CSV)') }}
          </main-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  GET_THEATER_SUBSCRIPTION_FINANCE_LEDGER,
  GET_THEATER_SUBSCRIPTION_FINANCE_SUMMARY,
  GET_THEATER_SUBSCRIPTION_REPORT_OPTIONS,
} from '@/graphql/theater-subscriptions.graphql'
import moment from 'moment-timezone'
import { theaterOrganizationRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'

const REPORT_TIMEZONE = 'Europe/Zagreb'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscription-reports',
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      financeFrom: undefined,
      financeTo: undefined,
      financeSummary: null,
      financeSummaryLoading: false,
      financeDownloadLoading: false,
      financeSummaryError: null,
      financeLedgerError: null,
      financeLedgerMessage: '',
      selectedSubscriptionId: null,
      selectedPaymentChannel: null,
      subscriptionPlans: [],
      plansLoading: false,
      plansError: null,
    }
  },
  computed: {
    filters() {
      const payload = {
        organizationSlug: this.organizationSlug,
      }
      const fromIso = this.dateToISO(this.financeFrom, true)
      if (fromIso) payload.from = fromIso
      const toIso = this.dateToISO(this.financeTo, false)
      if (toIso) payload.to = toIso
      if (this.selectedSubscriptionId) payload.subscriptionId = this.selectedSubscriptionId
      if (this.selectedPaymentChannel) payload.paymentChannel = this.selectedPaymentChannel

      return payload
    },
    subscriptionOptions() {
      return [
        { text: 'All subscriptions', value: null },
        ...this.subscriptionPlans.map(plan => ({ text: plan.name, value: plan.id })),
      ]
    },
    paymentChannelOptions() {
      return [
        { text: 'All sources', value: null },
        { text: 'Online', value: 'ONLINE' },
        { text: 'Box office', value: 'OFFLINE' },
      ]
    },
    selectedSubscription() {
      return this.subscriptionPlans.find(plan => plan.id === this.selectedSubscriptionId) || null
    },
    todayDate() {
      return moment().tz(REPORT_TIMEZONE).format('YYYY-MM-DD')
    },
    printableRoute() {
      return {
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscriptionsReportsPrint,
        params: { organizationSlug: this.organizationSlug },
        query: this.printableQuery,
      }
    },
    printableQuery() {
      const query = {}
      const fromIso = this.dateToISO(this.financeFrom, true)
      if (fromIso) {
        query.from = fromIso
      }
      const toIso = this.dateToISO(this.financeTo, false)
      if (toIso) {
        query.to = toIso
      }
      if (this.selectedSubscriptionId) {
        query.subscriptionId = String(this.selectedSubscriptionId)
      }
      if (this.selectedPaymentChannel) {
        query.paymentChannel = this.selectedPaymentChannel
      }

      return query
    },
  },
  created() {
    const todayInReportTimezone = moment().tz(REPORT_TIMEZONE)
    const startOfMonth = todayInReportTimezone.clone().startOf('month').format('YYYY-MM-DD')
    const today = todayInReportTimezone.format('YYYY-MM-DD')
    this.financeFrom = startOfMonth
    this.financeTo = today
  },
  mounted() {
    this.fetchSubscriptionPlans()
    this.fetchFinanceSummary()
  },
  methods: {
    async fetchSubscriptionPlans() {
      if (!this.organizationSlug) {
        return
      }
      this.plansLoading = true
      this.plansError = null
      try {
        const response = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_REPORT_OPTIONS,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'no-cache',
        })
        const data = response && response.data ? response.data : null
        this.subscriptionPlans =
          data && data.getTheaterSubscriptionReportOptions ? data.getTheaterSubscriptionReportOptions : []
      } catch (error) {
        this.subscriptionPlans = []
        this.plansError = 'Failed to load subscription filters.'
      } finally {
        this.plansLoading = false
      }
    },
    async fetchFinanceSummary() {
      if (!this.organizationSlug) {
        return
      }
      this.financeSummaryLoading = true
      this.financeSummaryError = null
      this.financeLedgerMessage = ''
      try {
        const response = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_FINANCE_SUMMARY,
          variables: { filters: this.filters },
          fetchPolicy: 'no-cache',
        })
        const data = response && response.data ? response.data : null
        this.financeSummary = data && data.getTheaterSubscriptionFinanceSummary
          ? data.getTheaterSubscriptionFinanceSummary
          : null
      } catch (error) {
        this.financeSummary = null
        this.financeSummaryError = error && error.message ? error.message : 'Failed to load finance summary.'
      } finally {
        this.financeSummaryLoading = false
      }
    },
    async downloadFinanceLedger() {
      if (!this.organizationSlug) {
        return
      }
      this.financeLedgerError = null
      this.financeLedgerMessage = ''
      this.financeDownloadLoading = true
      try {
        const response = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_FINANCE_LEDGER,
          variables: { filters: this.filters },
          fetchPolicy: 'no-cache',
        })
        const data = response && response.data ? response.data : null
        const rows = data && data.getTheaterSubscriptionFinanceLedger ? data.getTheaterSubscriptionFinanceLedger : []
        if (!rows.length) {
          this.financeLedgerMessage = 'No ledger records found for the selected period.'
        } else {
          const csvContent = this.buildLedgerCsv(rows)
          const filename = `subscription-ledger-${[
            this.organizationSlug,
            this.selectedSubscription ? this.slugify(this.selectedSubscription.name) : null,
            this.selectedPaymentChannel ? this.selectedPaymentChannel.toLowerCase() : null,
          ]
            .filter(Boolean)
            .join('-')}-${moment().tz(REPORT_TIMEZONE).format('YYYYMMDD-HHmmss')}.csv`
          this.triggerCsvDownload(csvContent, filename)
          this.financeLedgerMessage = this.organizationMessage('organizationAll.ledgerDownloaded', 'Ledger downloaded ({{rows}} rows).', { rows: rows.length })
        }
      } catch (error) {
        this.financeLedgerError = error && error.message ? error.message : 'Failed to download ledger.'
      } finally {
        this.financeDownloadLoading = false
      }
    },
    buildLedgerCsv(rows) {
      const headers = [
        'Order ID',
        'Order UUID',
        'Created At',
        'Invoice Number',
        'Fiscal JIR',
        'Payment Type',
        'Payment Channel',
        'Organization Slug',
        'Organization Name',
        'Subscription ID',
        'Subscription Name',
        'Quantity',
        'Unit Price',
        'Gross Amount',
        'Net Amount',
        'VAT Amount',
        'VAT Rate',
        'Currency',
      ]
      const csvRows = [headers.join(',')]
      rows.forEach(row => {
        const values = [
          row.orderId,
          row.orderUuid,
          this.formatDateTime(row.createdAt),
          row.invoiceNumber || '',
          row.fiscalJir || '',
          row.paymentType || '',
          row.paymentChannel || '',
          row.organizationSlug || '',
          row.organizationName || '',
          row.subscriptionId,
          row.subscriptionName || '',
          row.quantity,
          this.toFixedAmount(row.unitPrice),
          this.toFixedAmount(row.grossAmount),
          this.toFixedAmount(row.netAmount),
          this.toFixedAmount(row.vatAmount),
          this.toFixedAmount(row.vatRate),
          row.currency || '',
        ].map(value => this.escapeCsvField(value))
        csvRows.push(values.join(','))
      })

      return csvRows.join('\n')
    },
    triggerCsvDownload(content, filename) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    escapeCsvField(value) {
      if (value === null || value === undefined) {
        return ''
      }
      let stringValue = String(value)
      const startsLikeFormula =
        /^[=+@\t\r]/.test(stringValue) ||
        (/^-/.test(stringValue) && !/^-\d+(\.\d+)?$/.test(stringValue))
      if (startsLikeFormula) {
        stringValue = `'${stringValue}`
      }
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }

      return stringValue
    },
    slugify(value) {
      return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    formatDateTime(date) {
      return moment(date).tz(REPORT_TIMEZONE).format('YYYY-MM-DD HH:mm')
    },
    toFixedAmount(value) {
      if (value === null || value === undefined) {
        return '0.00'
      }

      return Number(value).toFixed(2)
    },
    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      })

      return formatter.format(amount || 0)
    },
    formatNumber(value) {
      const formatter = new Intl.NumberFormat('hr-HR')

      return formatter.format(value || 0)
    },
    parseReportDate(value) {
      if (typeof value === 'string') {
        const dateOnly = moment.tz(value, 'YYYY-MM-DD', true, REPORT_TIMEZONE)
        if (dateOnly.isValid()) {
          return dateOnly
        }
      }

      return moment(value).tz(REPORT_TIMEZONE)
    },
    dateToISO(value, startOfDay) {
      if (!value) {
        return undefined
      }
      const reportDate = this.parseReportDate(value)
      const date = startOfDay ? reportDate.startOf('day') : reportDate.endOf('day')

      return date.toISOString()
    },
  },
}
</script>

<style scoped>
.subscription-reports {
  width: 100%;
}

.report-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 684px;
  border-radius: 24px;
  border: 1px solid #d9d9d9;
  overflow: hidden;
}

.report-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: #f5f5f5;
}

.report-title {
  color: #666;
  font-family: 'Raleway', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  margin: 0;
}

.report-body {
  padding: 32px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.date-row,
.filter-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.date-row > *,
.filter-row > * {
  min-width: 0;
}

.date-row >>> .v-input,
.filter-row >>> .v-input {
  margin: 0;
}

.date-row >>> .v-input__slot,
.filter-row >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: none !important;
}

.date-row >>> .v-input__slot::before,
.date-row >>> .v-input__slot::after,
.filter-row >>> .v-input__slot::before,
.filter-row >>> .v-input__slot::after {
  display: none;
}

.date-row >>> .v-text-field__details,
.filter-row >>> .v-text-field__details {
  display: none;
}

.filter-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #b71c1c;
}

.filter-error button {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.refresh-row,
.actions-row {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.actions-row {
  gap: 8px;
  margin-top: -8px;
}

.refresh-row >>> .v-btn,
.actions-row >>> .v-btn {
  height: 48px;
  margin: 0;
  padding: 0 24px;
}

.refresh-row >>> .v-btn__content,
.actions-row >>> .v-btn__content {
  display: flex;
  gap: 8px;
}

.printable-link {
  text-decoration: none;
}

.summary-grid {
  display: grid;
  gap: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #666;
}

.summary-row strong {
  color: #0277bd;
  text-align: right;
}

.report-error {
  color: #db384c;
  margin: 0;
}

.report-message {
  color: #0277bd;
  margin: 0;
}

@media (max-width: 600px) {
  .report-header {
    padding: 20px;
  }

  .report-body {
    padding: 24px 20px;
  }

  .date-row,
  .filter-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
